import { FC } from 'react';
import dayjs from 'dayjs';
import { Button, Checkbox, Input } from 'antd';
import {
  CreditCardOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';

import {
  ClientType,
  GROUP_STATE_LABEL,
  PAYMENT_METHODS,
  PaymentMethod,
  TrainingGroup,
} from '../CourseEnrollment.const';
import css from '../CourseEnrollment.module.css';

interface Props {
  courseTitle: string;
  contractNumber: string;
  listenersCount: number;
  pricePerStudent: string;
  totalPrice: string;
  groupId: string;
  onGroupChange: (id: string) => void;
  consent: boolean;
  onConsentChange: (v: boolean) => void;
  clientType: ClientType;
  paymentMethod: PaymentMethod;
  onPaymentMethodChange: (m: PaymentMethod) => void;
  onPay: () => void;
  onViewContract: () => void;
  groups: TrainingGroup[];
  payLoading?: boolean;
  promoCode: string;
  onPromoCodeChange: (v: string) => void;
  promoValidState: 'idle' | 'validating' | 'valid' | 'invalid';
  discountKopecks: number | null;
  appliedPromoCode: string | null;
  basePriceFormatted: string;
}

const paymentIcon = (m: PaymentMethod) => {
  if (m === 'erip') return <QrcodeOutlined />;
  if (m === 'card') return <CreditCardOutlined />;
  return <FileTextOutlined />;
};

const GroupAndPaymentStep: FC<Props> = ({
  courseTitle,
  contractNumber,
  listenersCount,
  pricePerStudent,
  totalPrice,
  groupId,
  onGroupChange,
  consent,
  onConsentChange,
  clientType,
  paymentMethod,
  onPaymentMethodChange,
  onPay,
  onViewContract,
  groups,
  payLoading,
  promoCode,
  onPromoCodeChange,
  promoValidState,
  discountKopecks,
  appliedPromoCode,
  basePriceFormatted,
}) => {
  const selectedGroup = groups.find((g) => g.id === groupId);
  const paymentDeadline = selectedGroup
    ? selectedGroup.paymentDeadline ?? selectedGroup.startDate
    : null;
  const availableMethods = PAYMENT_METHODS.filter((m) =>
    clientType === 'individual'
      ? m.value !== 'requisites'
      : m.value === 'requisites',
  );

  return (
  <>
    <div className={css.TopSummary}>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Курс</span>
        <span className={css.TopSummaryValue}>{courseTitle}</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Номер договора</span>
        <span className={css.TopSummaryValue}>{contractNumber}</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Слушателей</span>
        <span className={css.TopSummaryValue}>{listenersCount} человек</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Цена за слушателя</span>
        <span className={css.TopSummaryValue}>{pricePerStudent}</span>
      </div>
      <div className={css.TopSummaryItem}>
        <span className={css.TopSummaryLabel}>Итого к оплате</span>
        <span className={css.TopSummaryValue}>{totalPrice}</span>
      </div>
    </div>

    <h3 className={css.SectionLabel}>Выберите группу обучения</h3>
    {groups.length === 0 ? (
      <p>Группы для этого курса пока не назначены</p>
    ) : (
      <div className={css.GroupGrid}>
        {groups.map((g) => {
          const isActive = g.id === groupId;
          return (
            <div
              key={g.id}
              className={`${css.GroupCard} ${
                isActive ? css.GroupCardActive : ''
              }`}
              onClick={() => onGroupChange(g.id)}
            >
              <div className={css.GroupCardHead}>
                <span
                  className={`${css.GroupRadio} ${
                    isActive ? css.GroupRadioActive : ''
                  }`}
                />
                <span className={css.GroupCardName}>{g.name}</span>
              </div>
              <div className={css.GroupField}>
                <span className={css.GroupFieldLabel}>Начало</span>
                <span className={css.GroupFieldValue}>{g.startDate}</span>
              </div>
              <div className={css.GroupField}>
                <span className={css.GroupFieldLabel}>Окончание</span>
                <span className={css.GroupFieldValue}>{g.endDate}</span>
              </div>
              <div className={css.GroupCardDivider} />
              <div className={css.GroupField}>
                <span className={css.GroupFieldLabel}>Состояние</span>
                <span className={css.GroupStateBadge}>
                  {GROUP_STATE_LABEL[g.state]}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    )}

    <div className={css.PaymentNotice}>
      <span className={css.NoticeIcon}>
        <InfoCircleOutlined />
      </span>
      <span>
        Срок оплаты — не позднее {paymentDeadline ?? '___'}.
      </span>
    </div>

    <div className={css.PromoSection}>
      <label htmlFor="promoCodeInput" className={css.PromoLabel}>Промокод</label>
      <div className={css.PromoRow}>
        <Input
          id="promoCodeInput"
          value={promoCode}
          onChange={(e) => onPromoCodeChange(e.target.value)}
          placeholder="Введите промокод"
          allowClear
          maxLength={64}
          style={{
            flex: 1,
            borderColor:
              promoValidState === 'valid' ? '#10b981'
              : promoValidState === 'invalid' ? '#ef4444'
              : promoValidState === 'validating' ? '#f59e0b'
              : '#d1d5db',
          }}
        />
      </div>
      {promoValidState === 'valid' && (
        <div className={css.PromoHint} style={{ color: '#10b981' }}>Промокод применён</div>
      )}
      {promoValidState === 'invalid' && (
        <div className={css.PromoHint} style={{ color: '#ef4444' }}>Неверный или недействительный промокод</div>
      )}

      {promoValidState === 'valid' && discountKopecks !== null && appliedPromoCode && (
        <div className={css.SummaryGrid} style={{ marginTop: 12 }}>
          <span className={css.SummaryLabel}>Базовая цена</span>
          <span>{basePriceFormatted}</span>
          <span className={css.SummaryLabel}>Скидка ({appliedPromoCode})</span>
          <span style={{ color: '#10b981', fontWeight: 600 }}>
            {`−${(discountKopecks / 100).toFixed(2).replace('.', ',')} BYN`}
          </span>
          <hr className={css.ReceiptDivider} style={{ gridColumn: '1 / -1' }} />
          <span className={css.SummaryLabel} style={{ color: '#1f2937', fontWeight: 600 }}>Итого к оплате</span>
          <span style={{ fontWeight: 600 }}>{totalPrice}</span>
        </div>
      )}
    </div>

    <Checkbox
      className={css.Consent}
      checked={consent}
      onChange={(e) => onConsentChange(e.target.checked)}
    >
      Я подтверждаю, что все перечисленные слушатели согласны на обработку
      персональных данных в соответствии с{' '}
      {/* Новая вкладка: уход со страницы обнуляет весь мастер (шаг, форма,
          слушатели живут в useState), возврат назад ронял пользователя на
          пустой шаг 1. stopPropagation — клик по ссылке внутри label чекбокса
          иначе переключал согласие. */}
      <a
        className={css.ConsentLink}
        href='/privacy'
        target='_blank'
        rel='noopener noreferrer'
        onClick={(e) => e.stopPropagation()}
      >
        Политикой обработки персональных данных
      </a>
      .
    </Checkbox>

    <div className={css.PrimaryActions}>
      <Button className={css.ViewContractBtn} onClick={onViewContract}>Посмотреть договор</Button>
      <Button className={css.PayBtn} disabled={!consent || payLoading} loading={payLoading} onClick={onPay}>
        Оплатить
      </Button>
    </div>

    <h3 className={css.PaymentMethodsLabel}>Выберите способ оплаты</h3>
    <div className={css.PaymentMethodsGrid}>
      {availableMethods.map((m) => (
        <button
          type='button'
          key={m.value}
          className={`${css.PaymentMethodCard} ${
            paymentMethod === m.value ? css.PaymentMethodCardActive : ''
          }`}
          onClick={() => onPaymentMethodChange(m.value)}
        >
          <span className={css.PaymentMethodIcon}>{paymentIcon(m.value)}</span>
          {m.label}
        </button>
      ))}
    </div>
  </>
  );
};

export default GroupAndPaymentStep;
